// Crisis keywords and content-moderation detection.
//
// The lists below are the BAKED-IN DEFAULTS, used when the Strapi-hosted
// override has not been fetched yet OR when the fetch failed. This is
// deliberate fail-open behavior (MWBI-666): a Strapi outage must never
// silently disable crisis-keyword detection — that would be a real safety
// regression for users disclosing self-harm or suicidal intent.
//
// At runtime, useModerationStore fetches the Strapi single-type
// `content-moderation` once on app boot and calls setModerationLists() to
// swap these defaults for whatever Jessica/Jenna currently have configured
// in the admin UI. No code redeploy needed to add/remove a word.
//
// MWBI-883 — two detection upgrades layered on top of the MWBI-666 plumbing:
//   1. Leet-speak / symbol obfuscation. The hard-block pass (inappropriate +
//      the three new categories) matches via a per-word "fuzzy" regex that
//      tolerates leet substitutions (sh1t), censored vowels (f*ck/f@ck),
//      spaced-out letters (f u c k), and trailing symbols (a$$) — while
//      preserving the PR #374 metacharacter-safety guard. The crisis substring
//      pass gets a lighter, length-preserving leet substitution.
//   2. Three new hard-block categories: hate speech, cyberbullying, and
//      graphic/trigger content. All four hard-block lists OR together into one
//      block decision (detectHardBlock), so every existing composer keeps
//      gating on a single signal. A curated allowlist suppresses benign
//      gaming/clinical phrases ("triple kill", "blood pressure").

const DEFAULT_CRISIS_KEYWORDS: readonly string[] = [
  // English
  'suicide',
  'suicidal',
  'self-harm',
  'self harm',
  'kill myself',
  'end my life',
  'want to die',
  'hurt myself',
  // MWBI-882 — additional crisis phrases from the Restricted Content guidelines
  // (section 1, Mental Health Crisis). Substring-matched and case-insensitive
  // like the rest. 'you should kill yourself' is intentionally the full phrase,
  // not the shorter 'kill yourself', so it catches encouragement-to-suicide
  // without tripping the common "don't kill yourself over it" idiom.
  'you should kill yourself',
  'kms',
  'overdose',
  'jump off',
  'cutting myself',
  'burning myself',
  'slice my wrists',
  'want to bleed',
  'no reason to live',
  "can't take it anymore",
  'final goodbye',
  'everyone is better off without me',
  'go hang yourself',
  'drink bleach',
  'ending it all',
  // Spanish
  'suicidio',
  'suicida',
  'autolesión',
  'autolesion',
  'autolesionarme',
  'hacerme daño',
  'matarme',
  'quitarme la vida',
  'acabar con mi vida',
  'terminar con mi vida',
  'quiero morir',
  'quiero morirme',
  'lastimarme',
  'no quiero vivir',
  // MWBI-882 — Spanish equivalents of the new crisis phrases above. PENDING
  // native-speaker / product (Jessica/Jenna) sign-off before merge. Accented
  // and unaccented variants are both listed because the detector does not fold
  // accents today (accent-insensitive matching is tracked in MWBI-883).
  'deberías matarte',
  'deberias matarte',
  'mátate',
  'matate',
  'sobredosis',
  'saltar al vacío',
  'saltar al vacio',
  'quemarme',
  'cortarme las venas',
  'quiero sangrar',
  'no hay razón para vivir',
  'no hay razon para vivir',
  'no puedo más',
  'no puedo mas',
  'despedida final',
  'todos estarían mejor sin mí',
  'todos estarian mejor sin mi',
  'ahórcate',
  'ahorcate',
  'bebe lejía',
  'bebe lejia',
  'toma cloro',
  'acabar con todo',
];

const DEFAULT_INAPPROPRIATE_WORDS: readonly string[] = [
  // English
  'fuck',
  'shit',
  'bitch',
  // MWBI-883 — 'ass' added so the guidelines' 'a$$' obfuscation example resolves.
  // The token boundaries keep it from tripping 'class', 'bass', 'assistant', etc.
  'ass',
  'asshole',
  'bastard',
  'cunt',
  'damn',
  'crap',
  'piss',
  'dick',
  'cock',
  'pussy',
  'nigger',
  'nigga',
  'faggot',
  'retard',
  'whore',
  'slut',
  // MWBI-882 — Restricted Content guidelines section 4. 'motherfucker' needs
  // its own entry: the \b…\b matcher does not match 'fuck' embedded inside it.
  // 'hell' is the guidelines' "mild" tier; \b keeps it from tripping
  // 'hello'/'shell' and only matches the standalone word.
  'motherfucker',
  'hell',
  // Spanish
  'mierda',
  'puta',
  'puto',
  'coño',
  'cabron',
  'cabrón',
  'pendejo',
  'pendeja',
  'joder',
  'chingar',
  'chingada',
  'chingado',
  'verga',
  'culo',
  'maricón',
  'maricon',
  'perra',
  'idiota',
  'imbecil',
  'imbécil',
  'hijoputa',
  'pene',
  'vagina',
];

// MWBI-883 — hate speech / hate crimes (hard block). Restricted Content
// guidelines §2: dehumanization + incitement + targeted harassment. Individual
// slurs already live in the inappropriate list above, so this list is the
// phrase-level and dehumanization entries that profanity matching misses.
// High-FP, open-ended incitement templates ("kill all …", "death to …") are
// intentionally deferred to admin curation rather than seeded as bare phrases.
// Spanish entries are STARTER translations PENDING native-speaker / product
// (Jessica/Jenna) sign-off before merge.
const DEFAULT_HATE_SPEECH_WORDS: readonly string[] = [
  // English
  'subhuman',
  'vermin',
  'parasites',
  'ethnic cleansing',
  'white power',
  'go back to your country',
  // Spanish (pending sign-off)
  'infrahumano',
  'infrahumanos',
  'alimañas',
  'parásitos',
  'limpieza étnica',
  'poder blanco',
  'regresa a tu país',
];

// MWBI-883 — cyberbullying / targeted harassment (hard block). Restricted
// Content guidelines §3. High context-risk; curated conservatively. Spanish
// entries PENDING native-speaker / product sign-off.
const DEFAULT_CYBERBULLYING_WORDS: readonly string[] = [
  // English
  'doxing',
  'doxxing',
  'swatting',
  'revenge porn',
  'stfu',
  'i know where you live',
  // Spanish (pending sign-off)
  'doxeo',
  'porno de venganza',
  'sé dónde vives',
];

// MWBI-883 — graphic content / trigger words (hard block). Restricted Content
// guidelines §5: violence/gore + sexual content. Ambiguous single words with
// heavy benign use ("murder", "blood", "kill") are intentionally NOT seeded —
// they belong to admin curation paired with the allowlist below. Spanish
// entries PENDING native-speaker / product sign-off.
const DEFAULT_GRAPHIC_CONTENT_WORDS: readonly string[] = [
  // English
  'torture',
  'mutilate',
  'decapitate',
  'behead',
  'beheading',
  'school shooting',
  'mass shooting',
  'molestation',
  'pedophile',
  'incest',
  'bestiality',
  'porn',
  'porno',
  'nsfw',
  // Spanish (pending sign-off)
  'tortura',
  'mutilar',
  'decapitar',
  'tiroteo escolar',
  'pedófilo',
  'incesto',
  'pornografía',
];

// MWBI-883 — false-positive allowlist (Restricted Content guidelines
// "Contextual Analysis"). Benign gaming/clinical phrases that CONTAIN a word an
// admin might add to a hard-block list. Entries are matched and stripped from
// the text before the hard-block pass runs, so the surrounding word no longer
// trips. Keep entries multi-word (>= 2 tokens): single-token false positives
// ("class" containing "ass") are already handled by the word boundaries.
// Spanish entries PENDING native-speaker / product sign-off.
const DEFAULT_ALLOWLIST: readonly string[] = [
  // English — gaming
  'triple kill',
  'double kill',
  'kill streak',
  'killing spree',
  // English — clinical / common idiom
  'blood pressure',
  'blood test',
  'blood type',
  'blood donation',
  'murder mystery',
  'murder of crows',
  // Spanish (pending sign-off)
  'presión arterial',
  'análisis de sangre',
  'tipo de sangre',
  'novela de misterio',
];

// Back-compat exports. Tests and any existing callers still import these
// constants directly; keeping them as the defaults preserves behavior.
export const CRISIS_KEYWORDS = DEFAULT_CRISIS_KEYWORDS;
export const INAPPROPRIATE_WORDS = DEFAULT_INAPPROPRIATE_WORDS;

// Live lists used by the detection functions. Mutated by setModerationLists
// when Strapi fetch completes. Start as the defaults so detection works
// before the fetch resolves (and stays working if the fetch fails).
let currentCrisisKeywords: readonly string[] = DEFAULT_CRISIS_KEYWORDS;
let currentInappropriateWords: readonly string[] = DEFAULT_INAPPROPRIATE_WORDS;
let currentHateSpeechWords: readonly string[] = DEFAULT_HATE_SPEECH_WORDS;
let currentCyberbullyingWords: readonly string[] = DEFAULT_CYBERBULLYING_WORDS;
let currentGraphicContentWords: readonly string[] = DEFAULT_GRAPHIC_CONTENT_WORDS;
let currentAllowlist: readonly string[] = DEFAULT_ALLOWLIST;

// ───────────────────────── Crisis substring pass ──────────────────────────

// Length-preserving, ASCII-only leet substitution applied to the INPUT text
// (not the keywords) before the crisis substring match. Each obfuscation
// digit/symbol maps to a single canonical letter, so 'su1c1de' -> 'suicide'
// and 'k1ll myself' -> 'kill myself'. One code point in, one out:
//   - we never collapse separators, so multi-word keywords keep their spaces
//     ('kill myself', 'quitarme la vida');
//   - we never strip diacritics, so accented Spanish keywords still match
//     ('hacerme daño' stays intact).
const CRISIS_LEET_MAP: Readonly<Record<string, string>> = {
  '1': 'i',
  '0': 'o',
  $: 's',
  '@': 'a',
  '3': 'e',
  '!': 'i',
  '5': 's',
  '7': 't',
  '4': 'a',
  '8': 'b',
  '9': 'g',
};

function normalizeForCrisis(text: string): string {
  let out = '';
  for (const ch of text.toLowerCase()) {
    out += CRISIS_LEET_MAP[ch] ?? ch;
  }
  return out;
}

export function detectCrisisKeywords(text: string): boolean {
  // Fast-path empty/whitespace text: it can never match, and per-field
  // moderation (MWBI-873) runs the detectors on several composer fields per
  // render — most of them empty at any given time — so skipping the list scan
  // avoids needless work on every keystroke.
  if (!text.trim()) return false;
  const normalized = normalizeForCrisis(text);
  return currentCrisisKeywords.some(keyword => normalized.includes(keyword));
}

// ──────────────────────── Hard-block fuzzy matcher ─────────────────────────

// Escape regex metacharacters so admin-edited words like 'c++', '(', or
// '[bad' don't throw SyntaxError when interpolated into a regex. Without this,
// a single stray admin keystroke takes down every composer that runs detection
// in render (PR #374 review).
function escapeForRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Leet equivalents per ASCII letter for the hard-block pass. Conservative on
// purpose — high-collision substitutions (2->z, +->t, |->l) are omitted because
// they false-match ordinary text far more than they catch real obfuscation.
const BLOCK_LEET_MAP: Readonly<Record<string, readonly string[]>> = {
  a: ['@', '4'],
  b: ['8'],
  e: ['3'],
  g: ['9'],
  i: ['1', '!'],
  l: ['1'],
  o: ['0'],
  s: ['$', '5'],
  t: ['7'],
};

// Censor characters that can stand in for an INTERIOR letter (the 'u' in
// f*ck / f@ck). Restricting these to interior letters of >=3-letter tokens is
// what keeps emails (me@site), markdown (**bold**), and 'C#' from matching.
const CENSOR_CHARS: readonly string[] = ['*', '#', '@'];

// Separator characters used both for the optional intra-word filler ('f u c k',
// 'f.u.c.k') and the required join between words of a phrase ('ethnic cleansing').
// Deliberately HORIZONTAL whitespace + punctuation only — NOT '\n'. Per-field
// moderation joins composer fields with '\n' (MWBI-873/881) precisely so a word
// cannot form across a field boundary; '\s' would include '\n' and let the
// filler bridge that boundary, which the cross-field tests forbid.
const SEPARATOR_CLASS = ' \\t._\\-';
const SEP_REQUIRED = `[${SEPARATOR_CLASS}]+`;
const FILLER_OPTIONAL = `[${SEPARATOR_CLASS}]*`;

// Boundary alphabet for the negative lookarounds. We replace \b with these
// because a token ending in a non-word symbol (a$$) has no trailing \b, yet we
// still want to reject embedded substrings ("class" must not match "ass"). The
// set is \w plus every leet/censor symbol that can appear inside a token.
const TOKEN_BOUNDARY_CLASS = 'A-Za-z0-9_@$!*#+';

// Members are drawn from a known-safe set (a-z, 0-9, @ $ ! * #) that is literal
// inside a character class — none are class metacharacters (] \ ^ -), so no
// per-member escaping is required here.
//
// `dropDigitLeet` removes the DIGIT leet forms (a->4, s->5, …) while keeping the
// symbol forms (@ $ !) and censor wildcards. It is set for short blocked words
// (< 4 ASCII letters): such a word whose every letter has a digit form would
// otherwise be reconstructed from a bare number — 'ass' (a->4, s->5) matching
// '455' (room/flight/model numbers, prices, phone fragments). Dropping only the
// digits still lets the guidelines' 'a$$' / '@ss' symbol examples resolve.
function buildLetterClass(letter: string, allowCensor: boolean, dropDigitLeet: boolean): string {
  const members = new Set<string>([letter]);
  for (const alt of BLOCK_LEET_MAP[letter] ?? []) {
    if (dropDigitLeet && /[0-9]/.test(alt)) continue;
    members.add(alt);
  }
  if (allowCensor) for (const c of CENSOR_CHARS) members.add(c);
  return `[${Array.from(members).join('')}]`;
}

// Build one anchored regex source for a single blocked word/phrase. Returns
// null for blank input so callers can skip it (an empty body would match the
// empty string everywhere). `allowCensor` is disabled for the allowlist so its
// phrases can't be smuggled past via censored letters.
function buildTokenPattern(word: string, allowCensor: boolean): string | null {
  const w = word.replace(/\s+/g, ' ').trim();
  if (!w) return null;

  // Short words (< 4 ASCII letters, e.g. 'ass') drop their digit leet forms so a
  // bare number can't reconstruct them (see buildLetterClass). Counted across
  // the whole entry; multi-word phrases always clear the threshold and keep
  // digit leet, since their non-leetable letters already prevent number matches.
  let asciiLetterCount = 0;
  for (let i = 0; i < w.length; i += 1) {
    if (/[a-z]/.test(w[i])) asciiLetterCount += 1;
  }
  const dropDigitLeet = asciiLetterCount < 4;

  // Censor wildcards are eligible only on an ASCII letter that is neither the
  // first nor last ASCII letter OF ITS OWN token, and only for tokens with at
  // least 3 ASCII letters. Eligibility is computed PER TOKEN (not across the
  // whole phrase) so a multi-word phrase like 'ethnic cleansing' cannot match
  // 'ethnic @leansing' — the leading/trailing letters of every word stay
  // exact. Single-word inputs are unaffected (token bounds == phrase bounds).
  const censorEligiblePos = new Array<boolean>(w.length).fill(false);
  if (allowCensor) {
    const markToken = (start: number, end: number): void => {
      const letters: number[] = [];
      for (let i = start; i < end; i += 1) {
        if (/[a-z]/.test(w[i])) letters.push(i);
      }
      if (letters.length >= 3) {
        for (let k = 1; k < letters.length - 1; k += 1) {
          censorEligiblePos[letters[k]] = true;
        }
      }
    };
    let tokenStart = 0;
    for (let i = 0; i <= w.length; i += 1) {
      if (i === w.length || w[i] === ' ') {
        markToken(tokenStart, i);
        tokenStart = i + 1;
      }
    }
  }

  let body = '';
  for (let i = 0; i < w.length; i += 1) {
    const ch = w[i];
    if (ch === ' ') {
      body += SEP_REQUIRED;
    } else if (/[a-z]/.test(ch)) {
      body += buildLetterClass(ch, censorEligiblePos[i], dropDigitLeet);
    } else {
      // Accented letters, digits, punctuation, admin-typed metacharacters:
      // matched literally via the PR #374 escape guard.
      body += escapeForRegex(ch);
    }
    // Optional intra-word filler between two adjacent non-space chars. Letters
    // are disjoint from the filler set, so this cannot cause catastrophic
    // backtracking. Never emitted adjacent to a space (the space already emits
    // a required separator).
    const next = w[i + 1];
    if (next !== undefined && ch !== ' ' && next !== ' ') {
      body += FILLER_OPTIONAL;
    }
  }
  if (!body) return null;
  return `(?<![${TOKEN_BOUNDARY_CLASS}])${body}(?![${TOKEN_BOUNDARY_CLASS}])`;
}

// Compile a whole word list into ONE alternation regex. Longest phrases first
// so the allowlist strips the most specific match at any position. Returns null
// for an all-empty list.
function compileListRegex(
  words: readonly string[],
  opts: { allowCensor: boolean; global: boolean },
): RegExp | null {
  const sorted = [...words].sort((a, b) => b.length - a.length);
  const patterns: string[] = [];
  for (const word of sorted) {
    const pattern = buildTokenPattern(word, opts.allowCensor);
    if (pattern) patterns.push(`(?:${pattern})`);
  }
  if (patterns.length === 0) return null;
  return new RegExp(patterns.join('|'), opts.global ? 'gi' : 'i');
}

// Compiled regexes, memoized against the active word-list reference. Per-field
// moderation (MWBI-873/876) runs detection in render across many composers, so
// rebuilding regexes per keystroke was wasted CPU. Each cache rebuilds only
// when its list reference changes — which is exactly when setModerationLists /
// resetModerationListsToDefaults swap in a new array — so it stays correct
// without manual invalidation. Each category keeps its OWN cache: never
// concatenate the lists (a fresh array each render would disable memoization).
interface RegexCache {
  forWords: readonly string[] | null;
  regex: RegExp | null;
}

const inappropriateCache: RegexCache = { forWords: null, regex: null };
const hateSpeechCache: RegexCache = { forWords: null, regex: null };
const cyberbullyingCache: RegexCache = { forWords: null, regex: null };
const graphicContentCache: RegexCache = { forWords: null, regex: null };
const allowlistCache: RegexCache = { forWords: null, regex: null };

function getCategoryRegex(words: readonly string[], cache: RegexCache): RegExp | null {
  if (cache.forWords !== words) {
    cache.forWords = words;
    cache.regex = compileListRegex(words, { allowCensor: true, global: false });
  }
  return cache.regex;
}

function getAllowlistRegex(): RegExp | null {
  if (allowlistCache.forWords !== currentAllowlist) {
    allowlistCache.forWords = currentAllowlist;
    // No censor wildcards for the allowlist, and global so replace strips every
    // occurrence.
    allowlistCache.regex = compileListRegex(currentAllowlist, { allowCensor: false, global: true });
  }
  return allowlistCache.regex;
}

// .test() on these non-global category regexes does not advance lastIndex, so
// the cached instances are safe to reuse across calls.
function matchesCategory(text: string, words: readonly string[], cache: RegexCache): boolean {
  const regex = getCategoryRegex(words, cache);
  return regex !== null && regex.test(text);
}

export function detectInappropriateLanguage(text: string): boolean {
  // Fast-path empty/whitespace text (see detectCrisisKeywords): skips the regex
  // scan for the empty composer fields that per-field moderation evaluates on
  // every keystroke. This detector covers the inappropriate list only (no
  // allowlist) — kept for back-compat with existing imports/tests.
  if (!text.trim()) return false;
  return matchesCategory(text.toLowerCase(), currentInappropriateWords, inappropriateCache);
}

export function detectHateSpeech(text: string): boolean {
  if (!text.trim()) return false;
  return matchesCategory(text.toLowerCase(), currentHateSpeechWords, hateSpeechCache);
}

export function detectCyberbullying(text: string): boolean {
  if (!text.trim()) return false;
  return matchesCategory(text.toLowerCase(), currentCyberbullyingWords, cyberbullyingCache);
}

export function detectGraphicContent(text: string): boolean {
  if (!text.trim()) return false;
  return matchesCategory(text.toLowerCase(), currentGraphicContentWords, graphicContentCache);
}

// Unified hard-block signal: any of the four blocking categories matches, after
// allowlisted phrases are stripped. This is what composers gate submission on,
// so a single check covers profanity + hate speech + cyberbullying + graphic
// content. The allowlist is stripped FIRST (replaced with a space so adjacency
// can't be manufactured), then each category regex runs on the remainder.
export function detectHardBlock(text: string): boolean {
  if (!text.trim()) return false;
  let scan = text.toLowerCase();
  const allowlist = getAllowlistRegex();
  if (allowlist) {
    scan = scan.replace(allowlist, ' ');
    if (!scan.trim()) return false;
  }
  return (
    matchesCategory(scan, currentInappropriateWords, inappropriateCache)
    || matchesCategory(scan, currentHateSpeechWords, hateSpeechCache)
    || matchesCategory(scan, currentCyberbullyingWords, cyberbullyingCache)
    || matchesCategory(scan, currentGraphicContentWords, graphicContentCache)
  );
}

// ──────────────────────────── List management ─────────────────────────────

export function setModerationLists(lists: {
  crisis?: readonly string[];
  inappropriate?: readonly string[];
  hateSpeech?: readonly string[];
  cyberbullying?: readonly string[];
  graphicContent?: readonly string[];
  allowlist?: readonly string[];
}): void {
  // Empty-list guard on every list: an admin clearing a field in the Strapi UI
  // cannot turn detection off — the baked-in defaults stay active (PR #374
  // fail-open contract). For the allowlist this errs toward MORE blocking
  // (defaults keep benign gaming/clinical phrases postable), which is the safe
  // direction.
  if (lists.crisis && lists.crisis.length > 0) {
    currentCrisisKeywords = lists.crisis;
  }
  if (lists.inappropriate && lists.inappropriate.length > 0) {
    currentInappropriateWords = lists.inappropriate;
  }
  if (lists.hateSpeech && lists.hateSpeech.length > 0) {
    currentHateSpeechWords = lists.hateSpeech;
  }
  if (lists.cyberbullying && lists.cyberbullying.length > 0) {
    currentCyberbullyingWords = lists.cyberbullying;
  }
  if (lists.graphicContent && lists.graphicContent.length > 0) {
    currentGraphicContentWords = lists.graphicContent;
  }
  if (lists.allowlist && lists.allowlist.length > 0) {
    currentAllowlist = lists.allowlist;
  }
}

export function resetModerationListsToDefaults(): void {
  currentCrisisKeywords = DEFAULT_CRISIS_KEYWORDS;
  currentInappropriateWords = DEFAULT_INAPPROPRIATE_WORDS;
  currentHateSpeechWords = DEFAULT_HATE_SPEECH_WORDS;
  currentCyberbullyingWords = DEFAULT_CYBERBULLYING_WORDS;
  currentGraphicContentWords = DEFAULT_GRAPHIC_CONTENT_WORDS;
  currentAllowlist = DEFAULT_ALLOWLIST;
}

export function getActiveCrisisKeywords(): readonly string[] {
  return currentCrisisKeywords;
}

export function getActiveInappropriateWords(): readonly string[] {
  return currentInappropriateWords;
}

export function getActiveHateSpeechWords(): readonly string[] {
  return currentHateSpeechWords;
}

export function getActiveCyberbullyingWords(): readonly string[] {
  return currentCyberbullyingWords;
}

export function getActiveGraphicContentWords(): readonly string[] {
  return currentGraphicContentWords;
}

export function getActiveAllowlist(): readonly string[] {
  return currentAllowlist;
}
